import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import { GOOGLE_API_KEY } from "../../../config/config";

// keep libraries outside to avoid reload warnings
const LIBRARIES = ["geometry"];

const PathLocator = ({ data }) => {
  const [routePath, setRoutePath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const directionsService = useRef(null);
  const routeCalculated = useRef(false);

  const mapStyles = {
    height: "400px",
    width: "100%",
  };

  // Extract & validate coordinates
  const coordinates = (data || [])
    .map((item) => {
      const lat = Number(item.location?.[1]);
      const lng = Number(item.location?.[0]);
      if (isNaN(lat) || isNaN(lng)) {
        console.warn("Invalid coordinates skipped:", item.location);
        return null;
      }
      return {
        lat,
        lng,
        iconUrl: item?.iconUrl,
        tag: item?.tag,
        isAttendence: item?.isAttendence,
      };
    })
    .filter(Boolean);

  const defaultCenter = coordinates[0] || { lat: 0, lng: 0 };
  useEffect(() => {
  if (!mapLoaded) return;
  routeCalculated.current = false;
  if (coordinates.length >= 2) {
    calculateRoute();
  } else {
    setRoutePath(coordinates);
  }
}, [data, mapLoaded]);

  // useEffect(() => {
  //   if (mapLoaded && coordinates.length >= 2 && !routeCalculated.current) {
  //     calculateRoute();
  //   }
  // }, [mapLoaded, coordinates]);

  const calculateRoute = () => {
    if (routeCalculated.current) return;

    setLoading(true);
    routeCalculated.current = true;

    if (!window.google || !window.google.maps) {
      console.error("Google Maps API not loaded");
      setLoading(false);
      setRoutePath(coordinates);
      return;
    }

    if (coordinates.length < 2) {
      setLoading(false);
      setRoutePath(coordinates);
      return;
    }

    if (!directionsService.current) {
      directionsService.current = new window.google.maps.DirectionsService();
    }

    const waypoints = coordinates.slice(1, -1).map((coord) => ({
      location: new window.google.maps.LatLng(coord.lat, coord.lng),
      stopover: true,
    }));

    const request = {
      origin: new window.google.maps.LatLng(
        coordinates[0].lat,
        coordinates[0].lng
      ),
      destination: new window.google.maps.LatLng(
        coordinates[coordinates.length - 1].lat,
        coordinates[coordinates.length - 1].lng
      ),
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    directionsService.current.route(request, (result, status) => {
      setLoading(false);
      if (status === window.google.maps.DirectionsStatus.OK) {
        const path = result.routes[0].overview_path.map((point) => ({
          lat: point.lat(),
          lng: point.lng(),
        }));
        setRoutePath(path);
      } else {
        console.error("Directions request failed:", status);
        setRoutePath(coordinates); // fallback to straight lines
      }
    });
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  return (
    <LoadScript googleMapsApiKey={GOOGLE_API_KEY} libraries={LIBRARIES}>
      <GoogleMap
        mapContainerStyle={mapStyles}
        zoom={coordinates.length > 0 ? 15 : 1}
        center={defaultCenter}
        onLoad={handleMapLoad}
      >
        {/* Markers */}
        {mapLoaded &&
          coordinates.map((coord, index) => (
            <Marker
              key={index}
              position={{ lat: coord.lat, lng: coord.lng }}
              icon={{
                url: coord.iconUrl,
                scaledSize: new window.google.maps.Size(30, 30),
                anchor: new window.google.maps.Point(6, 6),
              }}
              title={coord?.tag}
              label={
                coord.isAttendence
                  ? {
                      text: coord?.tag,
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      fontSize: "15px",
                    }
                  : {
                      text: coord?.tag,
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      fontSize: "12px",
                    }
              }
            />
          ))}

        {/* Polyline route */}
        {mapLoaded && routePath.length > 0 && (
          <Polyline
            path={routePath}
            options={{
              strokeColor: "#FF0000",
              strokeOpacity: 1.0,
              strokeWeight: 4,
              geodesic: true,
            }}
          />
        )}

        {/* Loading overlay */}
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(255,255,255,0.9)",
              padding: "5px 10px",
              borderRadius: "8px",
              border: "2px solid #FF0000",
              fontWeight: "bold",
              zIndex: 1000,
            }}
          >
            Loading actual road route...
          </div>
        )}
      </GoogleMap>
    </LoadScript>
  );
};

export default PathLocator;
